import React from 'react'
import { Heading, Text, Button } from '@primer/react-brand'

import PlanSummaryCard from '../components/PlanSummaryCard'
import { useCurrentOrganization } from '../../components/useCurrentOrganization'
import { planLabel, APPLICATION_REVIEW_WINDOW } from '../../components/subscriptionConstants'

// Shown when an org that has already requested approval (applied_at set,
// approved_at/rejected_at nil) re-enters the subscribe flow — via the home/
// pricing Subscribe CTAs, a direct /subscribe/* URL, the back button, or a
// refresh. Instead of letting it re-submit the Apply form, this confirms the
// request is in and explains what happens next.
//
// Design: AlreadyRequestedScreen in Prototype C_A_primer_v2.html (.ap-requested,
// .ap-next-panel). Mirrors ApplyForAccessPage's PlanSummaryCard composition.
// Routed by MainController#subscribe_requested. Renders content-only into the
// header-only app chrome (civic_lens_subscribe layout); no page Header/Footer.
//
// Props: { returnTo }
const AlreadyRequestedScreen = ({ returnTo }) => {
  const { org, loading } = useCurrentOrganization()

  // Loading shell.
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 40px' }}>
        <Text as="p" variant="muted">Loading…</Text>
      </div>
    )
  }

  // Guards, symmetric with ApplyForAccessPage. An approved org has no pending
  // request to show — straight into the app.
  if (org && org.approved_at) {
    if (typeof window !== 'undefined') window.location.href = '/monitoring'
    return null
  }
  // This screen only applies to a requested org. Anything else landed here
  // wrongly — back to pricing.
  const isRequested = org && (org.applied_at || org.status === 'application_pending')
  if (!isRequested) {
    if (typeof window !== 'undefined') window.location.href = '/membership'
    return null
  }

  const plan = (org.selected_plan || '').toLowerCase()
  const requester = org.applied_by?.name
  const requestedOn = org.applied_at ? new Date(org.applied_at).toLocaleDateString() : null

  const steps = [
    <>A CivicLens team member will review your application <strong style={{ color: 'var(--base-color-scale-gray-9)', fontWeight: 700 }}>in {APPLICATION_REVIEW_WINDOW}</strong>.</>,
    <>Once approved, your subscription will begin.</>,
  ]

  return (
    <div style={{ maxWidth: 960, width: '100%', margin: '0 auto', padding: '52px 40px 96px', fontFamily: 'var(--brand-body-fontFamily)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 56, alignItems: 'flex-start' }}>
          {/* Left — plan summary card */}
          <PlanSummaryCard
            plan={plan}
            footer={
              <Text as="p" style={{ margin: 0, fontSize: 12, color: 'var(--base-color-scale-gray-4)' }}>
                Your free trial continues until your application is approved.
              </Text>
            }
          />

          {/* Right — already-requested confirmation */}
          <div>
            <Heading as="h1" size="5">A subscription has already been requested for your organization</Heading>
            <Text as="p" size="300" variant="muted" style={{ marginTop: 6, marginBottom: 28, lineHeight: 1.65 }}>
              A CivicLens subscription is only available to eligible organizations approved by project staff. Approval is granted when an organization&rsquo;s use of CivicLens supports our mission to strengthen civic life in America.
            </Text>

            {/* Requested callout */}
            <div
              style={{
                background: 'var(--base-color-scale-gray-0)',
                border: '1px solid var(--base-color-scale-gray-1)',
                borderRadius: 8,
                padding: '18px 22px',
                marginBottom: 24,
              }}
            >
              <Text as="p" weight="semibold" style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: 'var(--base-color-scale-gray-9)' }}>
                Approval has been requested
              </Text>
              <Text as="p" style={{ margin: 0, fontSize: 14, color: 'var(--base-color-scale-gray-6)', lineHeight: 1.5 }}>
                {requestedOn ? `On ${requestedOn} approval was requested` : 'Approval was requested'} for a {planLabel(plan)} plan
                {requester ? ` by ${requester}` : ''}.
              </Text>
            </div>

            {/* What happens next — numbered steps */}
            <div style={{ marginBottom: 28 }}>
              <Text as="p" weight="semibold" style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: 'var(--base-color-scale-gray-9)' }}>
                What happens next
              </Text>
              <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 13 }}>
                {steps.map((step, index) => (
                  <li key={index} style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                    <span
                      style={{
                        flexShrink: 0,
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: 'var(--base-color-scale-blue-5)',
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {index + 1}
                    </span>
                    <span style={{ fontSize: 14, color: 'var(--base-color-scale-gray-7)', lineHeight: 1.5, paddingTop: 3 }}>
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            <Button
              variant="primary"
              onClick={() => { if (typeof window !== 'undefined') window.location.href = returnTo || '/monitoring' }}
              style={{
                backgroundColor: 'var(--base-color-scale-blue-5)',
                borderColor: 'var(--base-color-scale-blue-5)',
                borderRadius: '6px',
              }}
            >
              Got it
            </Button>
          </div>
        </div>
      </div>
  )
}

export default AlreadyRequestedScreen
